import { ReactTFC } from "../../utils/fp/missing-ts-parts";
import React from "react";
import { SelectSubfamilyLevel } from "./levels/1-select-subfamily-level";
import { SelectProductLevel } from "./levels/2-select-product-level";
import { ShowSparePartsProductLevel } from "./levels/3-show-spare-parts-product-level";

import { useSparePartsSubpage } from "./sitemap/use-plp-subpage";
import { ShowSparePartsScreenLevel } from "./levels/4-show-spare-parts-screen-level";
import { ShowSparePartsHeadline } from "./shared/show-spare-parts-headline";
import { useSparePartsClient } from "../../domain/storefront";
import { Spacer } from "../../components/_shared-components/arrangement/Space";
import { ExtractSwarovskiPageData, ProductListingPageData } from "@swo/storefront";
import { getSwarovskiOptikHeadTitle } from "../_layout-helpers/head-title";
import { PlpRootLevel } from "./levels/0-root-level";
import { T } from "../../components/T";

export const ProductListingPageContant: ReactTFC<
  ExtractSwarovskiPageData<ProductListingPageData> & {
    hasMetadata: boolean;
  }
> = props => {
  const {
    locale,
    segment,
    data: { translations: t },
  } = props;
  const { subpage, pathname, sitemapLinker } = useSparePartsSubpage();

  const client = useSparePartsClient({
    localeCode: locale.code,
    segmentKey: segment.key,
  });

  const HeadFallback = !props.hasMetadata && <title>{getSwarovskiOptikHeadTitle(t.sparePartsFinder.value)}</title>;

  switch (subpage.tag) {
    case "root":
    case "faceted-search": {
      return (
        <>
          <Spacer size={"lg"} />
          <PlpRootLevel {...props} hasMetadata={false} sitemapLinker={sitemapLinker} subpage={subpage} />
        </>
      );
    }

    case "category": {
      const { categoryKey } = subpage;

      return (
        <>
          {HeadFallback}
          <Spacer size={"lg"} />

          <ShowSparePartsHeadline headline={<T>{t.chooseCategory}</T>} translations={t} />
          <SelectSubfamilyLevel
            pathname={pathname}
            fetcher={() => client.forCategory({ categoryKey })}
            categoryKey={categoryKey}
            sitemapLinker={sitemapLinker}
            translations={t}
          />
        </>
      );
    }
    case "subfamily": {
      const { subfamilyKey } = subpage;

      return (
        <>
          <Spacer size={"lg"} />

          {HeadFallback}
          <ShowSparePartsHeadline headline={t.chooseModel.value} translations={t} />
          <SelectProductLevel
            pathname={pathname}
            fetcher={() => client.forSubfamily({ subfamilyKey })}
            sitemapLinker={sitemapLinker}
            subfamilyKey={subpage.subfamilyKey}
            categoryKey={subpage.categoryKey}
            translations={t}
          />
        </>
      );
    }
    case "product": {
      const { productKey } = subpage;

      return (
        <>
          <Spacer size={"lg"} />

          {HeadFallback}
          <ShowSparePartsHeadline headline={<T>{t.checkYourConfiguration}</T>} translations={t} />
          <ShowSparePartsProductLevel
            pathname={pathname}
            fetcher={() => client.forProducts({ productKey })}
            sitemapLinker={sitemapLinker}
            data={subpage}
            translations={t}
          />
        </>
    );
    }
    case "product-spare-parts": {
      const { productKey } = subpage;

      return (
        <>
          {HeadFallback}
          <ShowSparePartsScreenLevel
            pathname={pathname}
            fetcher={() => client.forProducts({ productKey })}
            sitemapLinker={sitemapLinker}
            data={subpage}
            translations={t}
          />
        </>
      );
    }
    default: {
      const _: never = subpage;

      return _;
    }
  }
};
